import { FC, Fragment, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Avatar,
  Box,
  DropdownMenu,
  Icon,
  Illustration,
  Menu,
  Panel,
  Select,
  SIZE,
  Stack,
  Text,
  useToggleTheme,
  useCSSTheme,
  Badge,
} from '@quinyx/ui';
import classNames from 'classnames';

import styles from './navbar.module.scss';
import { useUser } from '../../context/authenticatedUser';
import { CONFIG } from '../../api/config';
import { useLogout } from '../../hooks/authentication';
import { useTranslate } from '../../hooks/translate';
import { useAllSideMenuTrees } from '@/hooks/navigation/useSideMenuTree';
import { useIsCustomerManager } from '@/hooks/customerManagerToken';
import { supportedLanguages } from '@/i18n';

const NavBarLink: FC<{ href: string; text: string }> = ({ href, text }) => {
  const location = useLocation();

  return (
    <Link
      data-testid={`navbar-link-${text.toLowerCase()}`}
      to={href}
      className={classNames(styles.navLink, styles.navButton, {
        [styles.navLink__active]: location.pathname.includes(href),
      })}>
      <Text variant="headlineRegular" color="PrimaryOnMain" as="span">
        {text}
      </Text>
    </Link>
  );
};

const Navbar: FC = () => {
  const { firstname, lastname, systemAdmin } = useUser();
  const logout = useLogout();
  const toggleTheme = useToggleTheme();
  const { themeName } = useCSSTheme();
  const { t, changeLanguage, selectedLanguage } = useTranslate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const sideMenuTrees = useAllSideMenuTrees();
  const { isCustomerManager } = useIsCustomerManager();

  const navItems = [
    ...(systemAdmin || isCustomerManager
      ? [{ href: '/admins', text: t('navbar.admins') }]
      : []),
    { href: '/customers', text: t('navbar.customers') },
    ...(systemAdmin ? [{ href: '/support', text: t('navbar.support') }] : []),
  ];

  const visibleSideMenus = sideMenuTrees.filter(
    ({ hidden, links }) => !hidden && links.some((link) => !link.hidden),
  );

  return (
    <Stack
      as="header"
      className={styles.header}
      backgroundColor="PrimaryMain"
      py={SIZE.S}
      pl={SIZE.M}
      direction="row"
      alignItems="center"
      justifyContent="space-between">
      <Stack
        direction="row"
        alignItems="center"
        spacing={SIZE.S}
        fullWidth={false}>
        <Box className={styles.logoWrapper}>
          <Link
            to="/"
            className={styles.navButton}
            aria-label={t('navbar.home')}>
            <Box className={styles.qLogo}>
              <span className={styles.logoFull}>
                <Illustration.QLogo color="white" variant="full" />
              </span>
              <span className={styles.logoCompact}>
                <Illustration.QLogo color="white" variant="logo" />
              </span>
            </Box>
          </Link>
        </Box>

        <nav className={styles.desktopNav} aria-label={t('navbar.menu')}>
          <Stack direction="row" spacing={SIZE.S} fullWidth={false}>
            {navItems.map(({ href, text }) => (
              <NavBarLink key={href} href={href} text={text} />
            ))}
          </Stack>
        </nav>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        spacing={SIZE.S}
        fullWidth={false}>
        <div className={styles.mobileMenu}>
          <Panel
            open={menuOpen}
            onOpenChange={setMenuOpen}
            side="right"
            attach="screen"
            trigger={
              <button
                type="button"
                className={classNames(styles.navButton, styles.menuTrigger)}
                aria-label={t('navbar.menu')}
                data-testid="navbar-menu-button">
                <Icon icon="menu" color="PrimaryOnMain" />
              </button>
            }>
            <Panel.Header>
              <Panel.CloseButton
                dataTestId="navbar-menu-close"
                ariaLabel={t('common.close')}
              />
            </Panel.Header>
            <Panel.Content>
              <div className={styles.mobileNavMenu}>
                <Menu
                  data-test-id="navbar-mobile-menu"
                  aria-label={t('navbar.menu')}>
                  {visibleSideMenus.map(({ title, key, links }, index) => (
                    <Fragment key={key}>
                      {index > 0 && <Menu.Separator />}
                      <Menu.Label text={title} />
                      {links.map(({ id, route, linkLabel, hidden }) => {
                        if (hidden) return null;
                        return (
                          <Link
                            key={id}
                            to={route}
                            className={styles.mobileNavLink}
                            tabIndex={-1}
                            onClick={() => setMenuOpen(false)}>
                            <Menu.Item
                              data-test-id={`navbar-menu-sublink-${id}`}
                              selected={route === location.pathname}>
                              {linkLabel}
                            </Menu.Item>
                          </Link>
                        );
                      })}
                    </Fragment>
                  ))}
                </Menu>
              </div>
            </Panel.Content>
          </Panel>
        </div>

        <DropdownMenu
          trigger={
            <Avatar
              name={`${firstname} ${lastname}`}
              data-testid="navbar-avatar"
              tabIndex={0}
            />
          }>
          <Box p={SIZE.S}>
            <Text variant="smallHeavy">{t('navbar.account')}</Text>
            <Text>{`${firstname} ${lastname}`}</Text>
          </Box>
          <Box px={SIZE.S} pb={SIZE.S}>
            <Select
              name="language"
              label={t('navbar.language')}
              data-test-id="navbar-language-select"
              aria-label={t('navbar.selectLanguage')}
              value={selectedLanguage}
              placeholder={t('navbar.language')}
              onValueChange={changeLanguage}>
              {supportedLanguages.map(({ label, code }) => (
                <Select.Item
                  data-test-id={`navbar-language-select-item-${code}`}
                  key={code}
                  value={code}>
                  <Select.ItemText>{label}</Select.ItemText>
                </Select.Item>
              ))}
            </Select>
          </Box>
          <DropdownMenu.Separator />
          <DropdownMenu.Item onClick={toggleTheme}>
            {themeName === 'light'
              ? t('navbar.switchToDarkMode')
              : t('navbar.switchToLightMode')}
            {themeName === 'light' && (
              <Badge label={t('common.beta')} variant="negative" />
            )}
          </DropdownMenu.Item>
          <DropdownMenu.Item
            onClick={() => {
              window.location.href = CONFIG.MANAGER_PORTAL;
            }}>
            {t('navbar.switchToManagerPortal')}
          </DropdownMenu.Item>
          <DropdownMenu.Item onClick={logout}>
            {t('navbar.logout')}
          </DropdownMenu.Item>
        </DropdownMenu>
      </Stack>
    </Stack>
  );
};

export default Navbar;
